/**
 * Inspector — the umbrella that composes the shared show-page grammar
 * (design-system §5 / frontend-display §5). One grammar across ALL ~35 entities;
 * no bespoke per-entity screens.
 *
 *   <Inspector>
 *     <InspectorHeader title id status actions />
 *     <InspectorSection title="Overview"> <FieldGrid>…</FieldGrid> </InspectorSection>
 *     <InspectorSection title="Body">      …markdown / json…       </InspectorSection>
 *     <InspectorSection title="Relations"> <RelationsBlock …/>     </InspectorSection>
 *     <InspectorSection title="Audit">     <AuditBlock …/>         </InspectorSection>
 *   </Inspector>
 *
 * Everything here is **E0** (flush on the paper): structure comes from the
 * section heading style (§2.1) + `space-y-8` rhythm + `max-w-5xl`, not from
 * cards or borders. The Header carries at most **one** filled accent action; all
 * other actions are ghost or folded into a trailing `…` menu (§5.1).
 */
import * as React from "react";

import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "@/components/ui/icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/inspector/StatusDot";

/* ------------------------------------------------------------------ Header */

/** A secondary/overflow action descriptor for the Header `…` menu. */
export interface InspectorAction {
  label: string;
  onClick: () => void;
  icon?: React.ComponentType<{ className?: string }>;
  /** Tone the menu item destructive (e.g. Delete). */
  destructive?: boolean;
  disabled?: boolean;
}

/**
 * Click-to-copy monospace id. Per feedback #7 raw ids NEVER appear on the title
 * row / lead — this renders only deep in a show page's muted **Technical** tail
 * (used by `ShowPage`'s technical section and bespoke pages alike).
 */
export function TechnicalId({ id }: { id: string }) {
  const [copied, setCopied] = React.useState(false);
  const copy = React.useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    void navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }, [id]);
  return (
    <button
      type="button"
      onClick={copy}
      className="group/id inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground"
      title="Copy id"
    >
      {id}
      {copied ? (
        <Check className="h-3 w-3" aria-hidden />
      ) : (
        <Copy
          className="h-3 w-3 opacity-0 transition-opacity group-hover/id:opacity-100"
          aria-hidden
        />
      )}
      <span className="sr-only">Copy id</span>
    </button>
  );
}

export interface InspectorHeaderProps {
  /** Entity title — `text-2xl font-semibold`. */
  title: React.ReactNode;
  /**
   * One-line description under the title (the shared PageHeader subtitle — every
   * show page should pass one so all headers read identically; feedback #6).
   */
  subtitle?: React.ReactNode;
  /**
   * Accepted for back-compat but NO LONGER rendered in the header. Raw ids never
   * appear on the title row / lead (feedback #7) — they live only in the muted
   * Technical tail (`ShowPage` renders it there via `TechnicalId`).
   */
  id?: string;
  /** Lifecycle status string — rendered as a §10 `StatusBadge` (dot + word). */
  status?: string | null;
  /**
   * The single primary action (the main lifecycle verb). Rendered as the one
   * filled accent button. Keep it to one — restraint is what makes the accent
   * mean something (§5.1).
   */
  primaryAction?: React.ReactNode;
  /** Inline secondary actions (rendered as ghost buttons, left of the menu). */
  actions?: React.ReactNode;
  /** Overflow actions folded into a trailing `…` `DropdownMenu`. */
  menuActions?: InspectorAction[];
  className?: string;
}

/**
 * Inspector Header — title · id · StatusBadge · primary actions. A single E0
 * row, no card (§5.1).
 */
export function InspectorHeader({
  title,
  subtitle,
  // `id` intentionally destructured-and-ignored: kept in the prop type for
  // back-compat with ~35 call sites, but never rendered in the header (#7).
  id: _id,
  status,
  primaryAction,
  actions,
  menuActions,
  className,
}: InspectorHeaderProps) {
  return (
    <div
      className={cn(
        "group flex flex-wrap items-start justify-between gap-x-4 gap-y-3",
        className,
      )}
    >
      <div className="min-w-0 space-y-1.5">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground [overflow-wrap:anywhere]">
          {title}
        </h1>
        {subtitle ? (
          <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
            {subtitle}
          </p>
        ) : null}
        {status ? (
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={status} />
          </div>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {actions}
        {primaryAction}
        {menuActions && menuActions.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="More actions">
                <Icon name="more" size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {menuActions.map((action) => {
                const Icon = action.icon;
                return (
                  <DropdownMenuItem
                    key={action.label}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    className={cn(
                      action.destructive &&
                        "text-destructive focus:text-destructive",
                    )}
                  >
                    {Icon ? <Icon className="mr-2 h-4 w-4" /> : null}
                    {action.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- Section */

export interface InspectorSectionProps {
  /** Section heading (§2.1 style: `text-xs uppercase tracking-wide muted`). */
  title?: string;
  /** Right-aligned controls on the heading row (e.g. a count, a small action). */
  aside?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * A titled E0 section (Overview / Body / Relations / Audit). Heading + content;
 * no card, no border — `space-y-2` inside, `space-y-8` between sections.
 */
export function InspectorSection({
  title,
  aside,
  children,
  className,
}: InspectorSectionProps) {
  return (
    <section className={cn("space-y-3", className)}>
      {title || aside ? (
        <div className="flex items-center justify-between gap-2">
          {title ? (
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {title}
            </h3>
          ) : (
            <span />
          )}
          {aside}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/* --------------------------------------------------------------- Umbrella */

export interface InspectorProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * The Inspector page shell: caps content at `max-w-5xl`, `space-y-8` between
 * sections. Drop an `InspectorHeader` + a series of `InspectorSection`s inside.
 */
export function Inspector({ children, className }: InspectorProps) {
  return (
    <div className={cn("max-w-5xl space-y-8", className)}>{children}</div>
  );
}

export default Inspector;
